import React from "react";
import { Reveal, SectionOverline } from "./Shared";
import { Activity, Waves, Sparkles } from "lucide-react";

const WhatIsAdaptive = () => {
  return (
    <section
      id="what-is-adaptive"
      data-testid="what-is-adaptive-section"
      className="relative py-32 sm:py-40 border-t forr-canvas-alt overflow-hidden"
      style={{ borderColor: "var(--forr-line-soft)" }}
    >
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] forr-radial-teal opacity-60 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <SectionOverline
                number="03"
                label="What Adaptive Physiology Is"
                testId="what-is-adaptive-overline"
              />
            </Reveal>
            <Reveal delay={0.05}>
              <h2
                data-testid="what-is-adaptive-heading"
                className="mt-8 font-display forr-text-ink text-4xl sm:text-5xl md:text-6xl font-medium tracking-[-0.02em] leading-[1.05]"
              >
                A framework for changing behavior by{" "}
                <span className="forr-text-teal-deep">changing state.</span>
              </h2>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-7 lg:pl-8">
            <Reveal delay={0.1}>
              <div
                data-testid="adaptive-glass-card"
                className="rounded-[24px] p-8 lg:p-12 forr-surface"
                style={{
                  border: "1px solid var(--forr-line)",
                  boxShadow: "var(--forr-shadow-md)",
                }}
              >
                <p className="font-display text-2xl sm:text-3xl forr-text-ink font-medium leading-[1.25] tracking-[-0.01em]">
                  Adaptive Physiology™ is a science-informed methodology that
                  treats behavior as an{" "}
                  <span className="forr-text-teal-deep">output</span> of a
                  physiological state — a state that can be measured and{" "}
                  <span className="forr-text-teal-deep">
                    systematically retrained
                  </span>{" "}
                  through exercise physiology, behavioral conditioning, and
                  environmental adaptation.
                </p>

                <div className="mt-10 grid sm:grid-cols-3 gap-6">
                  {[
                    {
                      icon: Waves,
                      label: "State-first",
                      body: "We change the internal state before asking for new behavior.",
                      testId: "principle-state-first",
                    },
                    {
                      icon: Activity,
                      label: "Physiologically driven",
                      body: "Exercise physiology is the primary lever, not an accessory.",
                      testId: "principle-physiological",
                    },
                    {
                      icon: Sparkles,
                      label: "Adaptive by design",
                      body: "The nervous system rewrites itself when given the right stimulus.",
                      testId: "principle-adaptive",
                    },
                  ].map((p) => (
                    <div
                      key={p.label}
                      data-testid={p.testId}
                      className="p-5 rounded-2xl"
                      style={{
                        backgroundColor: "var(--forr-teal-tint)",
                        border: "1px solid var(--forr-line-soft)",
                      }}
                    >
                      <div className="forr-text-teal-deep mb-4">
                        <p.icon size={18} strokeWidth={1.5} />
                      </div>
                      <div className="font-display forr-text-ink font-medium text-base mb-2">
                        {p.label}
                      </div>
                      <div className="text-sm forr-text-soft font-light leading-relaxed">
                        {p.body}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatIsAdaptive;
